import { useState, useCallback, useRef } from 'react';

/**
 * Hook for managing block cluster/wallet actions
 * Handles API calls and polling for action status
 */
export function useBlockAction() {
  const [isBlocking, setIsBlocking] = useState(false);
  const [actionStatus, setActionStatus] = useState(null);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);
  const blockedItemsRef = useRef(new Set());

  // Actions API base URL - use the main API since endpoints are integrated there
  const actionsApiUrl = process.env.REACT_APP_ACTIONS_API_URL ||
    (window.location.hostname === 'localhost' ? 'http://localhost:3000' : '/api');

  /**
   * Poll action status until complete or failed
   */
  const pollActionStatus = useCallback(async (actionId) => {
    try {
      const response = await fetch(`${actionsApiUrl}/api/actions/${actionId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setActionStatus(data);

      // Continue polling if not in terminal state
      if (data.status !== 'CONFIRMED_ONCHAIN' && data.status !== 'FAILED') {
        pollingRef.current = setTimeout(() => pollActionStatus(actionId), 500);
      } else {
        setIsBlocking(false);

        // Track blocked items
        if (data.status === 'CONFIRMED_ONCHAIN') {
          if (data.clusterId) {
            blockedItemsRef.current.add(`cluster:${data.clusterId}`);
          }
          if (data.address) {
            blockedItemsRef.current.add(`wallet:${data.address.toLowerCase()}`);
          }
        }
      }
    } catch (err) {
      console.error('Error polling action status:', err);
      setError(err.message);
      setIsBlocking(false);
    }
  }, [actionsApiUrl]);

  /**
   * Block an entire cluster
   */
  const blockCluster = useCallback(async (clusterId) => {
    if (isBlocking) return;

    setIsBlocking(true);
    setError(null);
    setActionStatus(null);

    try {
      const response = await fetch(`${actionsApiUrl}/api/actions/block-cluster`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clusterId })
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Set initial status
      setActionStatus({
        id: data.actionId,
        kind: 'cluster',
        clusterId,
        status: 'VALIDATING'
      });

      // Start polling
      pollActionStatus(data.actionId);
    } catch (err) {
      console.error('Error blocking cluster:', err);
      setError(err.message);
      setIsBlocking(false);
    }
  }, [actionsApiUrl, isBlocking, pollActionStatus]);

  /**
   * Block a single wallet
   */
  const blockWallet = useCallback(async (address, clusterId = null) => {
    if (isBlocking) return;

    setIsBlocking(true);
    setError(null);
    setActionStatus(null);

    try {
      const response = await fetch(`${actionsApiUrl}/api/actions/block-wallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ address, clusterId })
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Set initial status
      setActionStatus({
        id: data.actionId,
        kind: 'wallet',
        address,
        clusterId,
        status: 'VALIDATING'
      });

      // Start polling
      pollActionStatus(data.actionId);
    } catch (err) {
      console.error('Error blocking wallet:', err);
      setError(err.message);
      setIsBlocking(false);
    }
  }, [actionsApiUrl, isBlocking, pollActionStatus]);

  /**
   * Check if a cluster is blocked
   */
  const isClusterBlocked = useCallback((clusterId) => {
    return blockedItemsRef.current.has(`cluster:${clusterId}`);
  }, []);

  /**
   * Check if a wallet is blocked
   */
  const isWalletBlocked = useCallback((address) => {
    return blockedItemsRef.current.has(`wallet:${address.toLowerCase()}`);
  }, []);

  /**
   * Reset action status (for dismissing UI)
   */
  const resetAction = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
    setActionStatus(null);
    setError(null);
    setIsBlocking(false);
  }, []);

  /**
   * Cleanup on unmount
   */
  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
  }, []);

  return {
    isBlocking,
    actionStatus,
    error,
    blockCluster,
    blockWallet,
    isClusterBlocked,
    isWalletBlocked,
    resetAction,
    cleanup
  };
}

export default useBlockAction;




